import { queryOptions, keepPreviousData, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { withPaginationSchema, parseSearchParams } from '@/api/utils';
import { PaginationParams } from '@/api/types';
import { ROLES } from '@/types/user';
import api from '../api';
import { User, UserSchema } from '@/api/schema/user';
import { RolePermissionsSchema } from '@/types/permissions';
import { getIdToken } from '@/lib/tokenManager';

export type { User };

export type UsersParams = {
  familyName?: string;
  givenName?: string;
  email?: string;
  role?: keyof typeof ROLES;
  siteId?: string;
  [key: string]: string | number | boolean | undefined;
} & PaginationParams;

function parseParams(params: UsersParams) {
  return parseSearchParams(params);
}

export function getUsersQueryOptions(params?: UsersParams) {
  return queryOptions({
    queryKey: [ 'users', params ],
    queryFn: async () => {
      if (!params) throw new Error('Params are required');
      const response = await api.get('users', {
        searchParams: parseParams(params),
      });
      return withPaginationSchema(UserSchema).parse(await response.json());
    },
    placeholderData: keepPreviousData,
  });
}

export function getUserQueryOptions(id: string) {
  return queryOptions({
    queryKey: [ 'users', id ],
    queryFn: async () => {
      const response = await api.get(`users/${id}`);
      return UserSchema.parse(await response.json());
    },
  });
}

export const CurrentUserSchema = z.object({
  user: UserSchema,
  permissions: RolePermissionsSchema,
});

export function getCurrentUserQueryOptions() {
  return queryOptions({
    queryKey: [ 'currentUser', getIdToken() ],
    queryFn: async () => {
      const response = await api.get(`auth/user`);
      const data = CurrentUserSchema.parse(await response.json());
      return { ...data.user, permissions: data.permissions };
    },
    enabled: !!getIdToken(),
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useUser() {
  return useSuspenseQuery(getCurrentUserQueryOptions());
}
